import { useEffect, type RefObject } from "react";

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

function focusableWithin(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    // `offsetParent` is null for anything `display:none`, which is the cheap way
    // to skip controls in a collapsed section without measuring every one.
    (element) => element.offsetParent !== null || element === document.activeElement,
  );
}

/**
 * Keeps Tab inside an open dialog and gives focus back to whatever opened it.
 *
 * Without the first half, tabbing out of a modal lands on the page behind it —
 * which is still there, still clickable to a screen reader, and now holds the
 * focus ring while a dialog is on screen. Without the second, dismissing a
 * dialog drops focus onto `<body>` and a keyboard user restarts from the top of
 * the document every time.
 */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const container = ref.current;
    if (!container) return;

    const previous = document.activeElement as HTMLElement | null;

    // The first control, or the dialog itself when it has none — an alert with
    // one "Close" is still a dialog and still needs the focus inside it.
    const initial = focusableWithin(container)[0] ?? container;
    initial.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Tab" || !container) return;
      const focusable = focusableWithin(container);
      if (focusable.length === 0) {
        event.preventDefault();
        container.focus();
        return;
      }

      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement;

      if (event.shiftKey && (active === first || active === container)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      // `isConnected`: the opener is often a row action that the same mutation
      // has just removed from the DOM, and focusing a detached node is a no-op
      // that silently loses the focus ring.
      if (previous?.isConnected) previous.focus();
    };
  }, [ref, active]);
}
